import moment from "moment-timezone";
import { formatDate } from "../../unixDateFormatter";
import { ColumnType } from "../../../data/column.enum";
import { Chips } from "@vibe/core";

// Column types whose Monday resync requires re-uploading attachments per
// linked item, so a single API call can take many seconds to complete.
export const SLOW_RESYNC_COLUMN_TYPES = new Set<string>([ColumnType.FILE, ColumnType.DOC]);

export const RESYNC_TOAST_INFO_DURATION_MS = 3000;
export const RESYNC_TOAST_SLOW_INFO_DURATION_MS = 6000;
export const RESYNC_TOAST_SUCCESS_DURATION_MS = 3000;
export const RESYNC_TOAST_ERROR_DURATION_MS = 5000;

export const hasSlowResyncColumn = (columnKeys: string[]): boolean =>
  columnKeys.some((key) => SLOW_RESYNC_COLUMN_TYPES.has(parseColumnKey(key).columnType));

// Helper function to format date with timezone conversion
const formatDateWithTimezone = (date: string, time?: string): string => {
  try {
    const userTimezone = moment.tz.guess();

    if (time) {
      // Combine date and time, assuming UTC
      const dateTimeString = `${date} ${time}`;
      const utcMoment = moment.utc(dateTimeString);

      if (utcMoment.isValid()) {
        return utcMoment.tz(userTimezone).format("MMM D, YYYY h:mm A");
      }
    }

    // If no time or invalid datetime, just format the date
    const dateMoment = moment.utc(date);
    if (dateMoment.isValid()) {
      return dateMoment.tz(userTimezone).format("MMM D, YYYY");
    }

    return date; // Fallback to original date string
  } catch (error) {
    console.error("Error formatting date with timezone:", error);
    return date; // Fallback to original date string
  }
};

// Helper function to find the most recent change across all boards
export const findMostRecentChange = (boards: any[]) => {
  // Flatten all activity logs from all boards
  const allActivityLogs = boards.reduce((acc: any[], board: any) => {
    // Monday returns activity_logs: null when an item was deleted or access was lost.
    // Defensively guard against that (and a null board entry) so such a board simply
    // contributes no logs instead of throwing.
    const logsWithBoardInfo = (board?.activity_logs ?? []).map((log: any) => ({
      ...log,
      boardId: board?.id,
      boardName: board?.name,
    }));
    return acc.concat(logsWithBoardInfo);
  }, []);

  // If no activity logs found, return null
  if (allActivityLogs.length === 0) {
    return null;
  }

  // Sort by created_at timestamp (descending - most recent first)
  // The timestamp appears to be in nanoseconds, so we can compare as strings or numbers
  const sortedLogs = allActivityLogs.sort((a: any, b: any) => {
    return parseInt(b.created_at) - parseInt(a.created_at);
  });

  const mostRecent = sortedLogs[0];

  // Parse the data JSON to extract current value
  let parsedData = null;
  let currentValue = null;
  let previousValue = null;

  try {
    parsedData = JSON.parse(mostRecent.data);
    currentValue = parsedData.value;
    previousValue = parsedData.previous_value;
  } catch (error) {
    console.error("Error parsing activity log data:", error);
  }

  return {
    ...mostRecent,
    parsedData,
    currentValue,
    previousValue,
    timestamp: formatDate(mostRecent.created_at), // Convert nanoseconds to milliseconds for Date
  };
};

export const formatColumnValueItemResync = (
  columnType: string,
  value: any,
  textualValue?: string,
  tooltip?: boolean,
) => {
  switch (columnType) {
    case "status":
    case "color":
      return value?.label?.text || "Empty Column";
    case "date":
      return value?.date ? formatDateWithTimezone(value.date, value?.time) : "Empty Column";
    case "text":
      return value?.value || "Empty Column";
    case "long-text":
    case "long_text":
      return value?.text || "Empty Column";
    case "number":
    case "numeric":
    case "numbers":
      return value?.value || "Empty Column";
    case "timeline":
    case "timerange":
      // Parse the dates
      const startDate = moment(value?.from);
      const endDate = moment(value?.to);
      const currentYear = moment().year(); //

      // Format based on conditions
      let formattedDateRange: string;
      if (startDate.isSame(endDate, "day")) {
        // Same date - display single date
        if (startDate.year() === currentYear) {
          formattedDateRange = startDate.format("MMM D");
        } else {
          formattedDateRange = startDate.format("MMM D, 'YY");
        }
      } else if (startDate.year() === endDate.year() && startDate.year() === currentYear) {
        // Same month and current year
        if (startDate.month() === endDate.month()) {
          formattedDateRange = `${startDate.format("MMM D")} - ${endDate.date()}`;
        } else {
          formattedDateRange = `${startDate.format("MMM D")} - ${endDate.format("MMM D")}`;
        }
      } else if (startDate.year() === endDate.year()) {
        // Same year but not the current year
        formattedDateRange = `${startDate.format("MMM D, 'YY")} - ${endDate.format("MMM D, 'YY")}`;
      } else {
        // Different years
        formattedDateRange = `${startDate.format("MMM D, 'YY")} - ${endDate.format("MMM D, 'YY")}`;
      }
      return formattedDateRange;
    case "phone":
      return value?.phone || "Empty Column";
    case "email":
      return value?.email || "Empty Column";
    case "dropdown":
      if (tooltip) {
        return value?.chosenValues.map((value: any) => (
          <div key={value.id} className="flex gap-2">
            {value.name}
          </div>
        ));
      }

      return (
        <>
          {value?.chosenValues.length > 0 ? (
            <div className="flex flex-col gap-2">
              {value?.chosenValues.map((value: any) => (
                <Chips readOnly={true} className="first:!ml-0" key={value.id} label={value.name} />
              ))}
            </div>
          ) : (
            "Empty Column"
          )}
        </>
      );
    case "link":
      return value?.url || "Empty Column";
    case "person":
    case "multiple-person":
    case "people":
      return textualValue || "Empty Column";
    case "rating":
      return `${value?.rating || 0}/5`;
    case "checkbox":
    case "boolean":
      return value?.checked ? "Checked" : "Unchecked";
    case "board_relation":
    case "board-relation":
      return textualValue || "Empty Column";
    case "location":
      return value?.address || "Empty Column";
    case "tag":
    case "tags":
      return value?.tags?.map((tag: any) => tag.name).join(", ") || "Empty Column";
    case "doc":
      return value?.files?.map((file: any) => file.name) || "Empty Column";
    case "file":
      return value?.files?.map((file: any) => file.name).join(", ") || "Empty Column";
    default:
      return "Empty";
  }
};

export const parseColumnKey = (key: string) => {
  // Get all possible column types from enum
  const columnTypes = Object.values(ColumnType);

  // Find the matching column type (longest match first to handle cases like 'board_relation' vs 'board')
  const sortedColumnTypes = columnTypes.sort((a, b) => b.length - a.length);

  for (const columnType of sortedColumnTypes) {
    if (key.startsWith(`${columnType}_`)) {
      const columnTitle = key.slice(columnType.length + 1); // +1 for the underscore
      return { columnType, columnTitle };
    }
  }

  // Fallback: split on first underscore
  const firstUnderscoreIndex = key.indexOf("_");
  if (firstUnderscoreIndex !== -1) {
    return {
      columnType: key.slice(0, firstUnderscoreIndex),
      columnTitle: key.slice(firstUnderscoreIndex + 1),
    };
  }

  // If no underscore found, treat the whole key as column title
  return { columnType: "", columnTitle: key };
};
